import {
  getSKUFromProductId,
  getPrintTypeFromProductId,
  getDesignerSettingsFromProductId,
  productHasBackDesignerSettings,
  isSub,
  getAdditionalTechniquesFromProductId,
} from "selectors/products";
import moment from "moment";
import { json2xmlPrintAreas } from "utils/jsonToXmlParserPrintAreas";

const regExpr = /[^a-zA-Z0-9- ]/g;

const json2xml = (
  order,
  products,
  email,
  auth = {},
  vatIncluded,
  iossNumber
) => {
  const {
    profile: { group, printLabel },
  } = auth;
  let items = "";
  let title = "";
  const isSam = order?.MasterUserEmail === "sam@bemerchful.com";
  const isBardownCAUser =
    order?.MasterUserEmail === "support@gobardown.com" &&
    order?.PrintProvider === "CA";

  for (let i = 0; i < order?.OrderLines[0]?.OrderLineItems.length; i++) {
    const orderLineItem = order?.OrderLines[0]?.OrderLineItems[i];
    let MockupSrc = orderLineItem?.MockupSrc;
    const marketPlaceDetails = orderLineItem?.MarketPlaceDetails;
    const properties = marketPlaceDetails?.properties;
    const isPersonlised = properties?.find((p) => p?.name === "Personalized");
    const logoType = properties?.find((p) => p?.name === "Logo Type");
    const salesValueInEuro = (
      (orderLineItem?.FulfillmentCost +
        (orderLineItem?.FulfillmentCost * 40) / 100) *
      1.18
    ).toFixed(2);
    const printAreas = orderLineItem?.printAreas;

    if (i === 0) {
      const printProvider = order?.PrintProvider;
      const selectedProduct = products?.find(
        (p) => p?.Product.ID === orderLineItem?.ProductID
      );
      const printProviders = selectedProduct?.PrintProviders;

      const shippingCosts = printProviders?.find(
        (s) => s?.ProviderName === printProvider
      )?.ShippingCosts;

      title = shippingCosts?.find(
        (p) => p?.ShippingDestination === order?.CountryString
      )?.Title;

      if (!title) {
        title = shippingCosts?.find(
          (p) => p?.ShippingDestination === "Intl"
        )?.Title;
      }
    }

    let sku = getSKUFromProductId(products, orderLineItem?.ProductID);

    if (isSam) {
      sku = orderLineItem?.MarketPlaceDetails?.sku;
    }

    if (isSam && sku.includes("of the Month")) {
      continue;
    }

    if (printAreas?.length > 0) {
      items += json2xmlPrintAreas(
        order,
        products,
        orderLineItem,
        vatIncluded,
        iossNumber
      );
      continue;
    }

    const isCertificateinSku = sku.includes("Certificate");
    const isDuffelBagInSku = sku.includes("Duffel Bag");
    const isPatchesInSku = sku.includes("Patches");

    const printType =
      orderLineItem?.PrintType ||
      getPrintTypeFromProductId(products, orderLineItem?.ProductID);
    let productName = orderLineItem?.Product || "";

    if (isSam) {
      productName = orderLineItem?.MarketPlaceDetails?.title;
    }

    const isOrderLineSub = isSub(products, orderLineItem?.ProductID);

    let designerSettings = getDesignerSettingsFromProductId(
      products,
      orderLineItem?.ProductID
    );

    if (printType !== "DTG") {
      designerSettings =
        getAdditionalTechniquesFromProductId(
          products,
          orderLineItem?.ProductID,
          false,
          printType
        ) || designerSettings;
    }

    let backDesignerSettings = productHasBackDesignerSettings(
      products,
      orderLineItem?.ProductID
    );

    if (printType !== "DTG") {
      backDesignerSettings =
        getAdditionalTechniquesFromProductId(
          products,
          orderLineItem?.ProductID,
          true,
          printType
        ) || backDesignerSettings;
    }

    const artWidth =
      parseFloat(
        orderLineItem?.ArtWorkCoordinates?.width * designerSettings?.multiplier
      ) || 0;
    const artBackWidth =
      parseFloat(
        orderLineItem?.BackArtWorkCoordinates?.width *
          backDesignerSettings?.multiplier
      ) || 0;

    const artHeight =
      parseFloat(
        orderLineItem?.ArtWorkCoordinates?.height * designerSettings?.multiplier
      ) || 0;

    const artBackHeight =
      parseFloat(
        orderLineItem?.BackArtWorkCoordinates?.height *
          backDesignerSettings?.multiplier
      ) || 0;

    const canvasCenterPoint = parseFloat(designerSettings?.width) / 2 || 0;
    const canvasBackCenterPoint =
      parseFloat(backDesignerSettings?.width) / 2 || 0;

    const x = parseFloat(orderLineItem?.ArtWorkCoordinates?.x) || 0;
    const xBack = parseFloat(orderLineItem?.BackArtWorkCoordinates?.x) || 0;

    const imageCenter =
      parseFloat(orderLineItem?.ArtWorkCoordinates?.width) / 2 || 0;
    const backImageCenter =
      parseFloat(orderLineItem?.BackArtWorkCoordinates?.width) / 2 || 0;

    let xPos;

    if (x > canvasCenterPoint) {
      xPos =
        canvasCenterPoint -
        parseFloat(orderLineItem?.ArtWorkCoordinates?.width) +
        imageCenter;
    } else if (x < canvasCenterPoint) {
      const newValue = x + imageCenter + 1;
      xPos = newValue === canvasCenterPoint ? 0 : newValue - canvasCenterPoint;
      xPos = xPos === 1 ? 0 : xPos;
    }

    let backXPos;
    if (xBack > canvasBackCenterPoint) {
      backXPos =
        canvasBackCenterPoint -
        parseFloat(orderLineItem?.BackArtWorkCoordinates?.width) +
        backImageCenter;
    } else if (xBack < canvasBackCenterPoint) {
      const newValue = xBack + backImageCenter + 1;
      backXPos =
        newValue === canvasBackCenterPoint
          ? 0
          : newValue - canvasBackCenterPoint;
      backXPos = backXPos === 1 ? 0 : backXPos;
    }

    var yPos = parseInt(orderLineItem?.ArtWorkCoordinates?.y);
    var backYPos = parseInt(orderLineItem?.BackArtWorkCoordinates?.y);

    let artwork = isSam
      ? orderLineItem?.Artwork
      : orderLineItem?.Artwork?.split("/upload/");
    if (artwork && !isSam) {
      artwork = `${artwork[0]}/upload/x_${Math.round(
        orderLineItem?.ArtWorkCoordinates?.x
      )},y_${Math.round(orderLineItem?.ArtWorkCoordinates?.y)}/${
        artwork[1]
      }?${moment().unix()}_front`;
    }

    let backArtwork = isSam
      ? orderLineItem?.BackArtwork
      : orderLineItem?.BackArtwork?.split("/upload/");
    if (backArtwork && !isSam) {
      backArtwork = `${backArtwork[0]}/upload/x_${Math.round(
        orderLineItem?.BackArtWorkCoordinates?.x
      )},y_${Math.round(orderLineItem?.BackArtWorkCoordinates?.y)}/${
        backArtwork[1]
      }?${moment().unix()}_back`;
    }

    let vat = vatIncluded
      ? `(VALUE = €${salesValueInEuro} / QTY = ${orderLineItem.OliQuantity}${
          iossNumber ? ` / IOSS = ${iossNumber}` : ""
        })`
      : "";

    if (!vat) vat = "";

    if (isSam) {
      MockupSrc = MockupSrc?.replace(".ai", ".png");
      if (
        MockupSrc ===
        "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/No_image_available.svg/2048px-No_image_available.svg.png"
      ) {
        MockupSrc = order?.OrderLines[0]?.OrderLineItems[0]?.MockupSrc?.replace(
          ".pdf",
          ".png"
        ).replace(".ai", ".png");
      }
    }

    const isCertificateInDetails =
      orderLineItem?.Details?.includes("Certificate");

    const dom = order?.OrderLines[0]?.OrderLineItems?.find((o) =>
      o?.Details?.includes("Driver of the Month")
    );

    const wom = order?.OrderLines[0]?.OrderLineItems?.find((o) =>
      o?.Details?.includes("Warehouse of the Month")
    );

    const name = marketPlaceDetails?.properties?.find(
      (p) => p?.name === "Name"
    );

    const monthYear = marketPlaceDetails?.properties?.find(
      (p) => p?.name?.includes("Year") || p?.name?.includes("Month")
    );

    items += `<Item>
    <SKU>${
      order?.vendorReferenceNumber ? `(${order?.vendorReferenceNumber}) ` : ""
    }${sku?.replace(regExpr, "")}</SKU>
    <Descrip>${
      isSam
        ? `[Stock - BeMerchful]${
            isCertificateInDetails && dom
              ? ` [DOM=${name?.value}${
                  monthYear
                    ? `(${monthYear.value?.replace(/[$@%&]/g, "")}})`
                    : ""
                }] `
              : ""
          }${
            isCertificateInDetails && wom
              ? ` [WOM=${name?.value}${
                  monthYear
                    ? `(${monthYear.value?.replace(/[$@%&]/g, "")}})`
                    : ""
                }] `
              : ""
          }`
        : orderLineItem?.stockProvider
        ? `[STOCK-${orderLineItem?.stockReferenceNumber?.replace(
            /[$@%&]/g,
            ""
          )}}]`
        : ""
    }${
      orderLineItem.DtgPlus
        ? `[${printType} ${
            !!isPersonlised
              ? ` - PERSONALIZED : ${isPersonlised?.value?.replace(
                  /[$@%&]/g,
                  ""
                )}}`
              : ""
          }] ${vat?.replace(/[$@%&]/g, "")}} ${productName?.replace(
            /[$@%&]/g,
            ""
          )}}`
        : orderLineItem.Plus
        ? `[TUFF PRINT TRANSFER] ${vat?.replace(
            /[$@%&]/g,
            ""
          )}} ${productName?.replace(/[$@%&]/g, "")}}`
        : `[${printType} ${
            !!isPersonlised
              ? ` - PERSONALIZED : ${isPersonlised?.value?.replace(
                  /[$@%&]/g,
                  ""
                )}}`
              : ""
          } ${
            printType === "ScreenPrint"
              ? ` - ${orderLineItem?.numberOfColors?.replace(
                  /[$@%&]/g,
                  ""
                )}} Colors`
              : ""
          }/${
            orderLineItem?.dtgColorName?.SwatchCode
          }] ${vat} ${productName?.replace(/[$@%&]/g, "")}`
    }</Descrip>
    <Color>${
      isSam
        ? orderLineItem?.MarketPlaceDetails?.variant_title
        : orderLineItem.Color
    }</Color>
    <Qty>${
      isSam
        ? orderLineItem?.MarketPlaceDetails?.quantity
        : orderLineItem.OliQuantity
    }</Qty>
    <Size>${
      isSam
        ? orderLineItem?.MarketPlaceDetails?.variant_title
        : orderLineItem.Size
    }</Size>
    <Type>${printType || sku}</Type>
    <Style>${sku.split("-")[0]?.replace(regExpr, "")}</Style>
    <ProcessMode>Piece</ProcessMode>
    ${
      (orderLineItem?.ArtworkId || MockupSrc) &&
      (logoType &&
      isSam &&
      !isCertificateinSku &&
      !isPatchesInSku &&
      !isDuffelBagInSku
        ? false
        : true)
        ? `
        <Design>
          <Location>FRONT</Location>
          <Thumb>${
            isCertificateinSku
              ? isOrderLineSub
                ? artwork?.replace(".jpg", ".png").replace(".ai", ".png") ||
                  MockupSrc?.replace(".jpg", ".png").replace(".ai", ".png")
                : artwork?.replace(".jpg", ".png").replace(".ai", ".png") ||
                  MockupSrc?.replace(".jpg", ".png").replace(".ai", ".png")
              : MockupSrc.replace(".ai", ".png")
          }</Thumb>
          <SourceFile>${
            isOrderLineSub
              ? artwork?.replace(".jpg", ".png").replace(".ai", ".png") ||
                MockupSrc?.replace(".jpg", ".png").replace(".ai", ".png")
              : artwork?.replace(".jpg", ".png").replace(".ai", ".png") ||
                MockupSrc?.replace(".jpg", ".png").replace(".ai", ".png")
          }</SourceFile>
          <Width>${Math.round(artWidth) || 0}</Width>
          <Height>${Math.round(artHeight) || 0}</Height>
          <LOffset>${
            Math.round(xPos * designerSettings?.multiplier) || 0
          }</LOffset>
          <TOffset>${
            Math.round(yPos * designerSettings?.multiplier) || 0
          }</TOffset>
          <WhiteUnderbaseRequired>false</WhiteUnderbaseRequired>
        </Design>`
        : ""
    }
    ${
      orderLineItem?.aopVectorFile
        ? `
        <Design>
          <Location>BACK</Location>
          <Thumb>${orderLineItem.MarketingImagePath}</Thumb>
          <SourceFile>${orderLineItem.MarketingImagePath}</SourceFile>
          <Width>0</Width>
          <Height>0</Height>
          <LOffset>0</LOffset>
          <TOffset>0</TOffset>
          <WhiteUnderbaseRequired>false</WhiteUnderbaseRequired>
        </Design>`
        : ""
    }
    ${
      orderLineItem?.BackArtworkId
        ? `
        <Design>
          <Location>Back</Location>
          <Thumb>${
            isCertificateinSku
              ? isOrderLineSub
                ? backArtwork.replace(".jpg", ".png")
                : backArtwork.replace(".jpg", ".png")
              : orderLineItem.MockupBackSrc
          }</Thumb>
          <SourceFile>${
            isOrderLineSub
              ? backArtwork.replace(".jpg", ".png")
              : backArtwork.replace(".jpg", ".png")
          }</SourceFile>
          <Width>${Math.round(artBackWidth)}</Width>
          <Height>${Math.round(artBackHeight)}</Height>
          <LOffset>${Math.round(
            backXPos * backDesignerSettings.multiplier
          )}</LOffset>
          <TOffset>${Math.round(
            backYPos * backDesignerSettings.multiplier
          )}</TOffset>
          <WhiteUnderbaseRequired>false</WhiteUnderbaseRequired>
        </Design>`
        : ""
    }`;

    if (!!isPersonlised) {
      items += `
          <Design>
            <Location>RIGHT CHEST</Location>
            <Thumb>https://images.shirtly.com/image/upload/l_text:Roboto_51_bold:${isPersonlised?.value},co_rgb:FFFFFF/e_trim/v1663335494/BOTApp/StaplesPO/PartnBrigham/5X5_vgx54x.png</Thumb>
            <SourceFile>https://images.shirtly.com/image/upload/l_text:Roboto_51_bold:${isPersonlised?.value},co_rgb:FFFFFF/e_trim/v1663335494/BOTApp/StaplesPO/PartnBrigham/5X5_vgx54x.png</SourceFile>
            <Width>1022</Width>
            <Height>511</Height>
            <LOffset>0</LOffset>
            <TOffset>0</TOffset>
            <WhiteUnderbaseRequired>false</WhiteUnderbaseRequired>
          </Design>`;
    }

    if (
      !!logoType &&
      !isCertificateinSku &&
      !isDuffelBagInSku &&
      !isPatchesInSku
    ) {
      items += `
          <Design>
            <Location>LEFT CHEST</Location>
            <Thumb>${
              isSam
                ? MockupSrc
                : logoType.value === "English"
                ? "https://images.shirtly.com/image/upload/v1665760618/BOTApp/sam%40bemerchful.com/ArtFiles/Ryder_Ever_Better_EN_2C_d8emdm.png"
                : "https://images.shirtly.com/image/upload/v1665760746/BOTApp/sam%40bemerchful.com/ArtFiles/Ryder_Ever_Better_FR_white_znwvqo.png"
            }</Thumb>
            <SourceFile>${
              logoType.value === "English"
                ? "https://images.shirtly.com/image/upload/v1665760618/BOTApp/sam%40bemerchful.com/ArtFiles/Ryder_Ever_Better_EN_2C_d8emdm.png"
                : "https://images.shirtly.com/image/upload/v1665760746/BOTApp/sam%40bemerchful.com/ArtFiles/Ryder_Ever_Better_FR_white_znwvqo.png"
            }</SourceFile>
            <Width>1022</Width>
            <Height>511</Height>
            <LOffset>0</LOffset>
            <TOffset>0</TOffset>
            <WhiteUnderbaseRequired>false</WhiteUnderbaseRequired>
          </Design>`;
    }

    items += `</Item>`;
  }

  return `<AddXml xmlns="http://tempuri.org/">
  <OrderString>
      <Request>
          <CustID>1</CustID>
          <PO>${order?.ReceiptID}</PO>
          <OrderDate>${order.DateCreated}</OrderDate>
          <Source>${order?.PickUp ? "Admin_PickUp" : "Admin"}</Source>
          <Store>BOT</Store>
          <StoreID>2</StoreID>
          <ShipTo>
              <FirstName>${order?.BuyerName?.replace(regExpr, "")}</FirstName>
              <LastName></LastName>
              <Company></Company>
              <Adrx1>${order?.AddressLineOne?.replace(regExpr, "")}</Adrx1>
              <Adrx2>${order?.AddressLineTwo?.replace(regExpr, "")}</Adrx2>
              <City>${order?.City?.replace(regExpr, "")}</City>
              ${
                order?.militaryAddress
                  ? `<State>${
                      order?.militaryAddress
                    } ${order?.StateProvince?.replace(regExpr, "")}</State>`
                  : `<State>${order?.StateProvince?.replace(
                      regExpr,
                      ""
                    )}</State>`
              } 
              <Zip>${order?.ZipPostal?.replace(regExpr, "")}</Zip>
              <Country>${order?.CountryString?.replace(regExpr, "")}</Country>
              <Email>${order?.MasterUserEmail}</Email>
          </ShipTo>
          ${
            order?.PrintProvider === "CA"
              ? `<ShipFrom>
                <FirstName>Big Oven Tees</FirstName>
                <LastName></LastName>
                <Company>Big Oven Tees</Company>
                <Adrx1>122 Middleton St.</Adrx1>
                <Adrx2></Adrx2>
                <City>Brantford</City>
                <State>ON</State>
                <Zip>N3S7V7</Zip>
                <Country>CA</Country>
                <Email>info@bigoventees.com</Email>
              </ShipFrom>`
              : `<ShipFrom>
              <FirstName>Big Oven Tees</FirstName>
              <LastName></LastName>
              <Company>Big Oven Tees USA</Company>
              <Adrx1>8742 Buffalo Avenue</Adrx1>
              <Adrx2></Adrx2>
              <City>Niagara Falls</City>
              <State>NY</State>
              <Zip>14304</Zip>
              <Country>US</Country>
              <Email>info@bigoventees.com</Email>
            </ShipFrom>`
          }
          ${
            isSam
              ? `<ShipMethod>${
                  order?.PrintProvider === "CA"
                    ? order?.order_data?.address_to?.country === "US"
                      ? "UPS - Ground Standard"
                      : order?.order_data?.address_to?.country === "CA"
                      ? "UPS - Ground Standard"
                      : "UPS - Ground Standard"
                    : "USPS First Class Mail"
                }</ShipMethod>          `
              : isBardownCAUser
              ? `<ShipMethod>UPS - Ground Standard</ShipMethod>`
              : `<ShipMethod>${
                  order?.PickUp
                    ? order?.PackingMode === "eco"
                      ? "(Eco-Bag) Pick Up "
                      : "Pick Up"
                    : order?.PackingMode === "eco"
                    ? `(Eco-Bag) ${title?.replace(regExpr, "")}`
                    : title?.replace(regExpr, "")
                }</ShipMethod>`
          }
          <ProductionPriority>Normal</ProductionPriority>
          ${
            order?.PickUp
              ? `<Notes>This is a Pickup Order.</Notes>`
              : `<Notes></Notes>`
          }
          ${items}
          
      </Request>
  </OrderString>
</AddXml>`;
};

export { json2xml };
